import type { Category } from "./categories";

/**
 * The company behind a product, and the word a brewer sees for it (Scott, 2026-09-29, in
 * rtylerdavis/BLCatalogDisplay, `notes/2026-09-29-maker-wording.md` and `hop-brand-vs-grower.md`).
 *
 * - A **Maltster** malts, whether or not its malt is what the brewery bought: Briess's extracts are a
 *   maltster's, and so are Muntons'.
 * - A hop company is the **Brand** on a hop as grown, the company on its item number (YCH, Charles Faram,
 *   HVG, BSG on its own-brand hops), and the **Manufacturer** of its extracts and oils, since nobody
 *   grew those in that form.
 * - Everyone else is a **Manufacturer**.
 *
 * The farm a lot of hops came from is its Grower: a fact about the lot, never the catalog.
 */
export const COMPANY_KINDS = ["maltster", "hop_company", "manufacturer"] as const;
export type CompanyKind = (typeof COMPANY_KINDS)[number];

/**
 * A hop's form, as the suppliers sell it. Part of what a hop product is: Cryo Mosaic and T-90 Mosaic are
 * two products, and so are a variety's pellets and its extract.
 */
export const HOP_FORMS = [
  "pellet",
  "cryo",
  "enriched_pellet",
  "whole_cone",
  "extract",
  "oil",
] as const;
export type HopForm = (typeof HOP_FORMS)[number];

export const HOP_FORM_LABELS: Record<HopForm, string> = {
  pellet: "T-90",
  cryo: "Cryo",
  enriched_pellet: "Enriched pellets",
  whole_cone: "Whole cone",
  extract: "Extract",
  oil: "Hop oil",
};

/** Forms a hop company made rather than grew: its Manufacturer's, not its Brand's. */
const MADE_FORMS: ReadonlySet<HopForm> = new Set(["extract", "oil"]);

/** The forms of a hop as grown: one item per variety and each of these (Scott, 2026-10-02). */
export const GROWN_FORMS: readonly HopForm[] = HOP_FORMS.filter((f) => !MADE_FORMS.has(f));

/**
 * A hop as grown (pellets, Cryo, enriched pellets, whole cones): in the catalog, a product of its
 * variety, where it grew and its form, with no maker; its Brand is on each item number.
 */
export function isGrownForm(form: string | null | undefined): form is HopForm {
  return isHopForm(form) && !MADE_FORMS.has(form);
}

export function isHopForm(value: string | null | undefined): value is HopForm {
  return (HOP_FORMS as readonly string[]).includes(value ?? "");
}

/**
 * Whether an item reads with its maker's short name in front ("Briess Pilsen Malt"). A hop as grown does
 * not: it is known by its variety alone, its form and brand shown beside the name and never in it
 * (Scott's Jam, 2026-10-02: "Hops should not be identified with these qualifiers tied to the variety
 * name"). A hop nobody gave a form is taken for one as grown. Extracts and oils are their manufacturer's
 * products, so they keep it, as everything else does.
 */
export function readsWithMaker(category: string, hopForm: string | null | undefined): boolean {
  return category !== "hop" || (isHopForm(hopForm) && MADE_FORMS.has(hopForm));
}

/**
 * Which variety a hop added by hand is, from its name, keyed as the catalog keys its own: "Simcoe",
 * "SIMCOE™" and "simcoe" are one. A hop from the catalog takes the catalog's key, which also knows two
 * spellings of one variety apart from two varieties; this only stands in where there is none.
 */
export function hopVarietyKey(name: string): string {
  return name
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .replace(/[®™©]/g, "")
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, " ")
    .trim();
}

/**
 * A hop as grown's form, where nothing beside the name says it: "Cryo". Brew day and a recipe's lines
 * show it, since a Cryo and a T-90 Citra both read "Citra" and go in at different amounts. Blank for an
 * extract or oil, whose name says what it is, and for anything that is not a hop.
 */
export function grownFormLabel(hopForm: string | null | undefined): string {
  return isGrownForm(hopForm) ? HOP_FORM_LABELS[hopForm] : "";
}

/** A name with its hop form after it, "Citra · Cryo", for plain text such as a list of names. */
export function withGrownForm(name: string, hopForm: string | null | undefined): string {
  const form = grownFormLabel(hopForm);
  return form ? `${name} · ${form}` : name;
}

export type MakerRole = "Maltster" | "Brand" | "Manufacturer";

export function isCompanyKind(value: string): value is CompanyKind {
  return (COMPANY_KINDS as readonly string[]).includes(value);
}

/**
 * The word for a company, given what it is and the product in question. Null for a company that only
 * sells, which is a Supplier and never the company behind a product.
 */
export function makerRole(
  kind: CompanyKind | null,
  category: Category,
  hopForm: HopForm | null = null,
): MakerRole | null {
  if (kind === null) return null;
  if (kind === "maltster") return "Maltster";
  if (kind === "hop_company") {
    return category === "hop" && !(hopForm && MADE_FORMS.has(hopForm)) ? "Brand" : "Manufacturer";
  }
  return "Manufacturer";
}
